import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import EmptyState from "@/components/base/EmptyState";
import { news } from "@/mocks/news";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const article = news.find((item) => item.id === id);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 影视资讯 - 白虎影视平台`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        meta.setAttribute("content", article.summary.slice(0, 140));
      }
    } else {
      document.title = "资讯不存在 - 白虎影视平台";
    }
  }, [article]);

  if (!article) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="资讯不存在"
          description="你访问的资讯可能已被删除或链接有误。返回资讯列表看看其他内容吧。"
          actionLabel="返回资讯列表"
          actionHref="/news"
        />
      </div>
    );
  }

  const related = news.filter((item) => item.id !== article.id).slice(0, 3);

  return (
    <article className="animate-fadeIn">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-4xl px-4 py-10 md:px-6 md:py-14">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/news" className="transition-colors hover:text-accent-600">
              影视资讯
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="truncate text-foreground-800">{article.category}</span>
          </nav>

          <span className="mt-6 inline-flex rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
            {article.category}
          </span>
          <h1 className="mt-4 text-2xl font-black leading-snug tracking-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              来源：{article.source}
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-10 md:px-6 md:py-12">
        <div className="aspect-[16/9] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} - 白虎影视资讯`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="mt-8 space-y-5">
          {article.content.map((paragraph, index) => (
            <p
              key={index}
              className="text-sm leading-loose text-foreground-700 md:text-base"
            >
              {paragraph}
            </p>
          ))}
        </div>

        <p className="mt-8 rounded-lg border border-background-200 bg-background-100 p-4 text-xs leading-relaxed text-foreground-500">
          声明：本页资讯内容为白虎影视平台演示内容，仅用于展示信息结构，不代表任何真实报道。
        </p>
      </div>

      <div className="bg-background-100">
        <div className="mx-auto max-w-4xl px-4 py-10 md:px-6 md:py-14">
          <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-200 text-base text-secondary-900">
              <i className="ri-links-line"></i>
            </span>
            相关推荐
          </h2>
          <ul className="mt-5 divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {related.map((item) => (
              <li key={item.id}>
                <Link
                  to={`/news/detail/${item.id}`}
                  className="flex flex-col gap-1 px-5 py-4 transition-colors hover:bg-background-100 sm:flex-row sm:items-center sm:justify-between"
                >
                  <span className="text-sm font-medium text-foreground-900 sm:text-base">
                    {item.title}
                  </span>
                  <span className="shrink-0 text-xs text-foreground-500">{item.publishDate}</span>
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-6">
            <Link
              to="/news"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-accent-400 hover:text-accent-600"
            >
              <i className="ri-arrow-left-line"></i>
              返回资讯列表
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}